import React, { useState, useEffect, useRef } from 'react';
import { X, Save, Image, Trash2, Clipboard } from 'lucide-react';
import apiService from './services/api';

function TrackingFormModal({ type, item, onClose, onSave }) {
  const isEditing = !!item;

  // Common fields
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState('');
  const [category, setCategory] = useState('');

  // Bug-specific fields
  const [severity, setSeverity] = useState('medium');
  const [affectedArea, setAffectedArea] = useState('');
  const [affectedFiles, setAffectedFiles] = useState('');
  const [rootCause, setRootCause] = useState('');
  const [investigationNotes, setInvestigationNotes] = useState('');
  const [fixApplied, setFixApplied] = useState('');
  const [fixCommit, setFixCommit] = useState('');
  const [preventionNotes, setPreventionNotes] = useState('');
  const [reportedBy, setReportedBy] = useState('');
  const [screenshot, setScreenshot] = useState('');
  const screenshotInputRef = useRef(null);

  // Feature-specific fields
  const [name, setName] = useState('');
  const [expectedBehavior, setExpectedBehavior] = useState('');
  const [apiEndpoints, setApiEndpoints] = useState('');
  const [frontendPages, setFrontendPages] = useState('');
  const [keyFiles, setKeyFiles] = useState('');
  const [dependencies, setDependencies] = useState('');

  // Idea-specific fields
  const [summary, setSummary] = useState('');
  const [priority, setPriority] = useState('medium');
  const [area, setArea] = useState('');
  const [problem, setProblem] = useState('');
  const [proposedSolution, setProposedSolution] = useState('');
  const [expectedBenefit, setExpectedBenefit] = useState('');
  const [implementationNotes, setImplementationNotes] = useState('');
  const [effort, setEffort] = useState('');
  const [impact, setImpact] = useState('');
  const [tags, setTags] = useState('');
  const [proposedBy, setProposedBy] = useState('');

  // Research-specific fields
  const [findings, setFindings] = useState('');
  const [dataEvidence, setDataEvidence] = useState('');
  const [methodology, setMethodology] = useState('');
  const [conclusion, setConclusion] = useState('');
  const [actionTaken, setActionTaken] = useState('');
  const [affectsProduction, setAffectsProduction] = useState(false);
  const [researcher, setResearcher] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [pasteSupported, setPasteSupported] = useState(false);

  // Check if Clipboard API is available (for iOS paste button)
  useEffect(() => {
    setPasteSupported('clipboard' in navigator && 'read' in navigator.clipboard);
  }, []);

  // Handle paste from clipboard button (for iOS)
  const handlePasteFromClipboard = async () => {
    try {
      const clipboardItems = await navigator.clipboard.read();
      for (const clipboardItem of clipboardItems) {
        for (const type of clipboardItem.types) {
          if (type.startsWith('image/')) {
            const blob = await clipboardItem.getType(type);
            const reader = new FileReader();
            reader.onload = (event) => {
              setScreenshot(event.target.result);
            };
            reader.readAsDataURL(blob);
            return;
          }
        }
      }
      alert('No image found in clipboard');
    } catch (err) {
      console.error('Clipboard read error:', err);
      alert('Could not read clipboard. Make sure you have copied an image.');
    }
  };

  // Load item data if editing
  useEffect(() => {
    if (item) {
      setTitle(item.title || '');
      setDescription(item.description || '');
      setStatus(item.status || '');
      setCategory(item.category || '');

      if (type === 'bugs') {
        setSeverity(item.severity || 'medium');
        setAffectedArea(item.affected_area || '');
        setAffectedFiles(item.affected_files || '');
        setRootCause(item.root_cause || '');
        setInvestigationNotes(item.investigation_notes || '');
        setFixApplied(item.fix_applied || '');
        setFixCommit(item.fix_commit || '');
        setPreventionNotes(item.prevention_notes || '');
        setReportedBy(item.reported_by || '');
        setScreenshot(item.screenshot || '');
      } else if (type === 'features') {
        setName(item.name || '');
        setExpectedBehavior(item.expected_behavior || '');
        setApiEndpoints(item.api_endpoints || '');
        setFrontendPages(item.frontend_pages || '');
        setKeyFiles(item.key_files || '');
        setDependencies(item.dependencies || '');
      } else if (type === 'ideas') {
        setSummary(item.summary || '');
        setPriority(item.priority || 'medium');
        setArea(item.area || '');
        setProblem(item.problem || '');
        setProposedSolution(item.proposed_solution || '');
        setExpectedBenefit(item.expected_benefit || '');
        setImplementationNotes(item.implementation_notes || '');
        setEffort(item.effort || '');
        setImpact(item.impact || '');
        setTags(item.tags || '');
        setProposedBy(item.proposed_by || '');
      } else if (type === 'research') {
        setSummary(item.summary || '');
        setFindings(item.findings || '');
        setDataEvidence(item.data_evidence || '');
        setMethodology(item.methodology || '');
        setConclusion(item.conclusion || '');
        setActionTaken(item.action_taken || '');
        setAffectsProduction(item.affects_production || false);
        setResearcher(item.researcher || '');
      }
    } else {
      // Set default status for new items
      if (type === 'bugs') setStatus('open');
      else if (type === 'features') setStatus('active');
      else if (type === 'ideas') setStatus('proposed');
      else if (type === 'research') setStatus('validated');
    }
  }, [item, type]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validate required fields
    if (type === 'bugs' && (!title || !description)) {
      setError('Title and description are required');
      return;
    }
    if (type === 'features' && (!name || !description || !expectedBehavior)) {
      setError('Name, description, and expected behavior are required');
      return;
    }
    if (type === 'ideas' && (!title || !summary || !category)) {
      setError('Title, summary, and category are required');
      return;
    }
    if (type === 'research' && (!title || !summary || !findings || !category)) {
      setError('Title, summary, findings, and category are required');
      return;
    }

    setSubmitting(true);

    try {
      let data = {};

      if (type === 'bugs') {
        data = {
          title,
          description,
          status,
          severity,
          category,
          affected_area: affectedArea || null,
          affected_files: affectedFiles || null,
          root_cause: rootCause || null,
          investigation_notes: investigationNotes || null,
          fix_applied: fixApplied || null,
          fix_commit: fixCommit || null,
          prevention_notes: preventionNotes || null,
          reported_by: reportedBy || null,
          screenshot: screenshot || null
        };
      } else if (type === 'features') {
        data = {
          name,
          description,
          expected_behavior: expectedBehavior,
          status,
          category: category || null,
          api_endpoints: apiEndpoints || null,
          frontend_pages: frontendPages || null,
          key_files: keyFiles || null,
          dependencies: dependencies || null
        };
      } else if (type === 'ideas') {
        data = {
          title,
          summary,
          category,
          priority,
          status,
          area: area || null,
          problem: problem || null,
          proposed_solution: proposedSolution || null,
          expected_benefit: expectedBenefit || null,
          implementation_notes: implementationNotes || null,
          effort: effort || null,
          impact: impact || null,
          tags: tags || null,
          proposed_by: proposedBy || null
        };
      } else if (type === 'research') {
        data = {
          title,
          summary,
          findings,
          category,
          status,
          area: area || null,
          data_evidence: dataEvidence || null,
          methodology: methodology || null,
          conclusion: conclusion || null,
          action_taken: actionTaken || null,
          affects_production: affectsProduction,
          tags: tags || null,
          researcher: researcher || null
        };
      }

      if (isEditing) {
        switch (type) {
          case 'bugs':
            await apiService.updateBug(item.id, data);
            break;
          case 'features':
            await apiService.updateFeature(item.id, data);
            break;
          case 'ideas':
            await apiService.updateIdea(item.id, data);
            break;
          case 'research':
            await apiService.updateResearch(item.id, data);
            break;
          default:
            break;
        }
      } else {
        switch (type) {
          case 'bugs':
            await apiService.createBug(data);
            break;
          case 'features':
            await apiService.createFeature(data);
            break;
          case 'ideas':
            await apiService.createIdea(data);
            break;
          case 'research':
            await apiService.createResearch(data);
            break;
          default:
            break;
        }
      }

      onSave();
    } catch (err) {
      setError(err.message || 'Failed to save');
      console.error('Error saving:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const renderFormFields = () => {
    switch (type) {
      case 'bugs':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
                >
                  <option value="open">Open</option>
                  <option value="investigating">Investigating</option>
                  <option value="fixed">Fixed</option>
                  <option value="wont_fix">Won't Fix</option>
                  <option value="regression">Regression</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Severity</label>
                <select
                  value={severity}
                  onChange={(e) => setSeverity(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
                >
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <input
                type="text"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
                placeholder="e.g., ui, backend, database"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Affected Area</label>
              <input
                type="text"
                value={affectedArea}
                onChange={(e) => setAffectedArea(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Affected Files</label>
              <textarea
                value={affectedFiles}
                onChange={(e) => setAffectedFiles(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800 font-mono text-sm"
                placeholder="List of affected files"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Root Cause</label>
              <textarea
                value={rootCause}
                onChange={(e) => setRootCause(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Investigation Notes</label>
              <textarea
                value={investigationNotes}
                onChange={(e) => setInvestigationNotes(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fix Applied</label>
              <textarea
                value={fixApplied}
                onChange={(e) => setFixApplied(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fix Commit</label>
              <input
                type="text"
                value={fixCommit}
                onChange={(e) => setFixCommit(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800 font-mono"
                placeholder="commit hash"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Prevention Notes</label>
              <textarea
                value={preventionNotes}
                onChange={(e) => setPreventionNotes(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reported By</label>
              <input
                type="text"
                value={reportedBy}
                onChange={(e) => setReportedBy(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Screenshot</label>
              <div
                className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
                  screenshot ? 'border-green-500 bg-green-50' : 'border-gray-300 hover:border-red-800 hover:bg-red-50'
                }`}
                onPaste={(e) => {
                  const items = e.clipboardData?.items;
                  if (items) {
                    // Check if there's an image in the clipboard
                    let hasImage = false;
                    for (let item of items) {
                      if (item.type.startsWith('image/')) {
                        hasImage = true;
                        const file = item.getAsFile();
                        if (file) {
                          const reader = new FileReader();
                          reader.onload = (event) => {
                            setScreenshot(event.target.result);
                          };
                          reader.readAsDataURL(file);
                        }
                        break;
                      }
                    }
                    // Only prevent default if we actually handled an image
                    if (hasImage) {
                      e.preventDefault();
                    }
                  }
                }}
                onClick={() => screenshotInputRef.current?.click()}
                tabIndex={0}
              >
                <input
                  ref={screenshotInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      const reader = new FileReader();
                      reader.onload = (event) => {
                        setScreenshot(event.target.result);
                      };
                      reader.readAsDataURL(file);
                    }
                  }}
                />
                {screenshot ? (
                  <div className="space-y-2">
                    <img
                      src={screenshot}
                      alt="Screenshot preview"
                      className="max-h-48 mx-auto rounded border border-gray-200"
                    />
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setScreenshot('');
                      }}
                      className="inline-flex items-center px-3 py-1 text-sm text-red-600 hover:text-red-800"
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Remove
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2 text-gray-500">
                    <Image className="w-8 h-8 mx-auto opacity-50" />
                    <p className="text-sm">Click to upload or paste an image</p>
                    <p className="text-xs text-gray-400">PNG, JPG, GIF supported</p>
                    {pasteSupported && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePasteFromClipboard();
                        }}
                        className="inline-flex items-center px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm mt-2"
                      >
                        <Clipboard className="w-4 h-4 mr-1" />
                        Paste from Clipboard
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </>
        );

      case 'features':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Expected Behavior *</label>
              <textarea
                value={expectedBehavior}
                onChange={(e) => setExpectedBehavior(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
                >
                  <option value="active">Active</option>
                  <option value="deprecated">Deprecated</option>
                  <option value="planned">Planned</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
                >
                  <option value="">Select category</option>
                  <option value="algorithms">Algorithms</option>
                  <option value="admin">Admin</option>
                  <option value="data-import">Data Import</option>
                  <option value="ui">UI</option>
                  <option value="api">API</option>
                  <option value="infrastructure">Infrastructure</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">API Endpoints</label>
              <textarea
                value={apiEndpoints}
                onChange={(e) => setApiEndpoints(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800 font-mono text-sm"
                placeholder="e.g., GET /api/users, POST /api/users"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Frontend Pages</label>
              <input
                type="text"
                value={frontendPages}
                onChange={(e) => setFrontendPages(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
                placeholder="e.g., /users, /dashboard"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Key Files</label>
              <textarea
                value={keyFiles}
                onChange={(e) => setKeyFiles(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800 font-mono text-sm"
                placeholder="List of key files implementing this feature"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Dependencies</label>
              <input
                type="text"
                value={dependencies}
                onChange={(e) => setDependencies(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
                placeholder="Related features or systems"
              />
            </div>
          </>
        );

      case 'ideas':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Summary *</label>
              <textarea
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
                required
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                <input
                  type="text"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                <select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
                >
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                  <option value="someday">Someday</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
                >
                  <option value="proposed">Proposed</option>
                  <option value="approved">Approved</option>
                  <option value="in_progress">In Progress</option>
                  <option value="implemented">Implemented</option>
                  <option value="rejected">Rejected</option>
                  <option value="deferred">Deferred</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Area</label>
              <input
                type="text"
                value={area}
                onChange={(e) => setArea(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Problem</label>
              <textarea
                value={problem}
                onChange={(e) => setProblem(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
                placeholder="What problem does this idea solve?"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Proposed Solution</label>
              <textarea
                value={proposedSolution}
                onChange={(e) => setProposedSolution(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
                placeholder="How would you solve this problem?"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Expected Benefit</label>
              <textarea
                value={expectedBenefit}
                onChange={(e) => setExpectedBenefit(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Implementation Notes</label>
              <textarea
                value={implementationNotes}
                onChange={(e) => setImplementationNotes(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Effort</label>
                <input
                  type="text"
                  value={effort}
                  onChange={(e) => setEffort(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
                  placeholder="e.g., small, medium, large"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Impact</label>
                <input
                  type="text"
                  value={impact}
                  onChange={(e) => setImpact(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
                  placeholder="e.g., low, medium, high"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
              <input
                type="text"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
                placeholder="Comma-separated tags"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Proposed By</label>
              <input
                type="text"
                value={proposedBy}
                onChange={(e) => setProposedBy(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
              />
            </div>
          </>
        );

      case 'research':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Summary *</label>
              <textarea
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Findings *</label>
              <textarea
                value={findings}
                onChange={(e) => setFindings(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
                required
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
                  required
                >
                  <option value="">Select category</option>
                  <option value="payments">Payments</option>
                  <option value="payroll">Payroll</option>
                  <option value="scheduling">Scheduling</option>
                  <option value="leads">Leads</option>
                  <option value="analytics">Analytics</option>
                  <option value="operations">Operations</option>
                  <option value="integrations">Integrations</option>
                  <option value="infrastructure">Infrastructure</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
                >
                  <option value="validated">Validated</option>
                  <option value="invalidated">Invalidated</option>
                  <option value="pending">Pending</option>
                  <option value="superseded">Superseded</option>
                  <option value="archived">Archived</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Area</label>
                <input
                  type="text"
                  value={area}
                  onChange={(e) => setArea(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Data Evidence</label>
              <textarea
                value={dataEvidence}
                onChange={(e) => setDataEvidence(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800 font-mono text-sm"
                placeholder="Data, logs, or metrics supporting findings"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Methodology</label>
              <textarea
                value={methodology}
                onChange={(e) => setMethodology(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
                placeholder="How was this research conducted?"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Conclusion</label>
              <textarea
                value={conclusion}
                onChange={(e) => setConclusion(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Action Taken</label>
              <textarea
                value={actionTaken}
                onChange={(e) => setActionTaken(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
                placeholder="What actions were taken based on this research?"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="affectsProduction"
                  checked={affectsProduction}
                  onChange={(e) => setAffectsProduction(e.target.checked)}
                  className="w-4 h-4 text-red-800 border-gray-300 rounded focus:ring-red-800"
                />
                <label htmlFor="affectsProduction" className="ml-2 text-sm font-medium text-gray-700">
                  Affects Production
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Researcher</label>
                <input
                  type="text"
                  value={researcher}
                  onChange={(e) => setResearcher(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
              <input
                type="text"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
                placeholder="Comma-separated tags"
              />
            </div>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg w-full max-w-3xl mx-4 max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-gradient-to-r from-red-800 to-red-900 text-white px-6 py-4 rounded-t-lg flex items-center justify-between sticky top-0">
          <h2 className="text-xl font-bold">
            {isEditing ? 'Edit' : 'Create'} {type.charAt(0).toUpperCase() + type.slice(1, -1)}
          </h2>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          <div className="space-y-4">
            {renderFormFields()}

            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg">
                {error}
              </div>
            )}
          </div>
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3 sticky bottom-0 bg-white rounded-b-lg">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={submitting}
            className="px-4 py-2 bg-red-800 text-white rounded-lg hover:bg-red-900 transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                {isEditing ? 'Update' : 'Create'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default TrackingFormModal;
